import { useEffect } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import { useNavigate, useParams } from 'react-router-dom';

import { Breadcrumbs } from '#client/components/Breadcrumbs';
import DebitCardInfo from '#client/components/DebitCardInfo';
import DebitCardPaymentContainer from '#client/components/DebitCardPaymentContainer';
import { DisableLayer } from '#client/components/DisableLayer';
import { FocusGrid, HubMainWrapperStyled, HubContainerGridStyled } from '#client/components/Grid';
import Notifications from '#client/components/Notifications';
import useAstraProfileContext from '#client/hooks/useAstraProfileContext';
import useBankingPath from '#client/hooks/useBankingPath';
import useDisableDashboardContext from '#client/hooks/useDisableDashboardContext';
import { BreadcrumbWrapStyled } from '#client/pages/SharedStyles';
import { formatAccountNumber } from '#client/utils/text';

const { isWebView } = WebView;

export default function DebitCardTransactionHistory() {
  const bankingPath = useBankingPath();
  const navigate = useNavigate();
  const { debitCardUuid } = useParams();
  const { isDisable } = useDisableDashboardContext();
  const { cardList, loading, error } = useAstraProfileContext();

  const dashboardPath = `${bankingPath}/dashboard`;
  const foundCard = (cardList ?? []).find((card) => card?.uuid === debitCardUuid);
  const {
    cardCompany,
    cardLastFourDigits,
    cardHolderFirstName,
    cardHolderLastName,
    cardBillingAddress,
    cardExpirationDate,
    isCardValidForTransfers,
    isCardExpired,
    uuid
  } = foundCard ?? {};
  const formatCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits ?? '',
    formatCharacter: '•',
    numberFormatCharacter: 2
  });
  const currentRouteText = cardCompany ? [cardCompany, formatCardNumber].join(' ') : '';

  useEffect(() => {
    const forceRedirect = error || (!loading && !foundCard);

    if (forceRedirect) {
      navigate(dashboardPath, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboardPath, error, loading, foundCard]);

  return (
    <HubContainerGridStyled>
      <FocusGrid>
        <HubMainWrapperStyled>
          {!isWebView && (
            <BreadcrumbWrapStyled>
              <Breadcrumbs
                items={[
                  {
                    to: '../../finances',
                    label: 'Finances'
                  },
                  {
                    to: '../dashboard',
                    label: 'Banking'
                  },
                  {
                    label: 'Debit Cards'
                  },
                  {
                    label: currentRouteText
                  }
                ]}
              />
            </BreadcrumbWrapStyled>
          )}
          <Notifications />
          <Box position="relative">
            {isDisable && <DisableLayer aria-label="Disable layer" />}
            <DebitCardInfo
              cardCompany={cardCompany}
              cardExpirationDate={cardExpirationDate}
              cardHolderFirstName={cardHolderFirstName}
              cardHolderLastName={cardHolderLastName}
              cardLastFourDigits={cardLastFourDigits}
              isCardExpired={isCardExpired}
              isCardValidForTransfers={isCardValidForTransfers}
              uuid={uuid}
            />
            <DebitCardPaymentContainer
              cardInfo={{
                cardBillingAddress,
                cardCompany,
                cardHolderFirstName,
                cardHolderLastName,
                cardLastFourDigits
              }}
            />
          </Box>
        </HubMainWrapperStyled>
      </FocusGrid>
    </HubContainerGridStyled>
  );
}
